import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import {
  DebouncedSearchInput,
  ListHeaderCloseButton,
  ListHeaderSearchButton,
  useScreenWidth,
} from "@msys/ui";
import { Alert, Box, Container, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { difference } from "lodash-es";
import React from "react";
import { Outlet, useLocation, useMatch, useNavigate } from "react-router-dom";
import { useLatest, useUpdateEffect } from "react-use";
import {
  Agreement,
  namedOperations,
  PermissionName,
} from "../../../clients/graphqlTypes.js";
import { useFeature } from "../../../common/FeatureFlags.js";
import { useUserData } from "../../auth/useUserData.js";
import { usePageWidth } from "../../commons/hooks/usePageWidth.js";
import {
  BreadcrumbItem,
  Page,
  PageTopbarItem,
} from "../../commons/layout/Page.js";
import { PageColumn } from "../../commons/layout/PageColumn.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { PageHeader } from "../../commons/layout/PageHeader.js";
import {
  ConfirmModalProps,
  ConfirmProcess,
  ConfirmProcessRef,
} from "../../commons/modals/ConfirmProcess.js";
import { QUOTE_ITEM_TYPES } from "../../constants.js";
import { QuoteAnnotationsBanner } from "../../features/quote-annotations/QuoteAnnotationsBanner.js";
import { ProfitabilityBox } from "../../features/doc-items/boxes/ProfitabilityBox.js";
import { ButtonItemBindingSelectMultiple } from "../../features/doc-items/fields/ButtonItemBindingSelectMultiple.js";
import { ButtonItemTypeSelectMultiple } from "../../features/doc-items/fields/ButtonItemTypeSelectMultiple.js";
import {
  getViewerDecisionRole,
  isTreePreviewItemVisible,
} from "../../features/doc-items/helpers.js";
import type { ItemChange } from "../../features/doc-items/hooks/useItemBindings.js";
import { useItemsSearch } from "../../features/doc-items/hooks/useItemsSearch.js";
import { ItemSearchChips } from "../../features/doc-items/ItemSearchChips.js";
import { DecisionsInBulkProcessRef } from "../../features/doc-items/modals/DecisionsInBulkProcess.js";
import {
  createTreeItem,
  createTreeItemInput,
  getEmptyPlaceholderRowAnnotation,
} from "../../features/projects/quote-trees.js";
import { QuoteChangeDeclinedAlert } from "../../features/quotes/alerts/QuoteChangeDeclinedAlert.js";
import { QuoteDeclinedAlert } from "../../features/quotes/alerts/QuoteDeclinedAlert.js";
import { QuoteProductsAvailabilityAlert } from "../../features/quotes/alerts/QuoteProductsAvailabilityAlert.js";
import {
  allowHaveChildren,
  shouldRenderCreateInput,
} from "../../features/quotes/helpers.js";
import { QuoteAction } from "../../features/quotes/QuoteAction.js";
import { QuoteDecisionsInBulkProcess } from "../../features/quotes/QuoteDecisionsInBulkProcess.js";
import { QuotePreview } from "../../features/quotes/QuotePreview.js";
import { QuotePriceSummaryBox } from "../../features/quotes/QuotePriceSummaryBox.js";
import { QuoteSubHeader } from "../../features/quotes/QuoteSubHeader.js";
import { QuoteTimeSummaryBox } from "../../features/quotes/QuoteTimeSummaryBox.js";
import { useCreateQuoteItems } from "../../features/quotes/useCreateQuoteItems.js";
import { useHasSapS4HanaIntegration } from "../../features/sap-s4-hana/hooks.js";
import { VanMarckeProfitability } from "../../features/van-marcke-profitability/VanMarckeProfitability.js";
import { VirtualBareTreeStandaloneItem } from "../../trees-virtual/components/VirtualBareTreeStandaloneItem.js";
import { getEliminatedItemsFilter } from "../../trees-virtual/helpers.js";
import { useEliminatedItemsStoreWithLocalStorage } from "../../trees-virtual/hooks/useEliminatedItemsStore.js";
import { useEnrichExpandedStoreWithParentPathIds } from "../../trees-virtual/hooks/useEnrichExpandedStoreWithParentPathIds.js";
import { useExpandedStoreWithLocalStorage } from "../../trees-virtual/hooks/useExpandedStore.js";
import {
  AdditionalItemInput,
  TreeViewMode,
} from "../../trees-virtual/types.js";
import { VirtualItemTree } from "../../trees-virtual/VirtualItemTree.js";
import { useMoveTreeItem } from "../../trees-virtual/hooks/useMoveTreeItem.js";
import {
  TreeToggleAllExpandedButton,
  TreeToggleEliminatedItemsButton,
} from "../../trees/components/TreeToggleButton.js";
import {
  createRecursiveTryFocusElementFunction,
  falseFn,
  isInTestMode,
  nullFn,
} from "../../utils.js";
import { DocShareBox } from "../documents/DocShareBox.js";
import { useProjectQuoteContext } from "./ProjectQuoteContext.js";
import {
  ProjectQuoteEdit_ItemFragment,
  useProjectQuoteEditQuery,
  useProjectQuoteEditQuoteProductsStatusQuery,
} from "./ProjectQuoteEdit.generated.js";

const REFETCH_QUERIES = [
  namedOperations.Query.ProjectQuoteEdit,
  namedOperations.Query.ProjectQuoteEditQuoteProductsStatus,
];

interface Props {
  projectId: string;
  quoteId: string;
  prefixBreadcrumbs: BreadcrumbItem[];
  submenuItems: PageTopbarItem[];
  activeSubmenuItem: PageTopbarItem | undefined;
}

export const ProjectQuoteEdit = ({
  projectId,
  quoteId,
  submenuItems,
  activeSubmenuItem,
  prefixBreadcrumbs,
}: Props) => {
  const pathToDocList = `/projects/${projectId}/quotes`;
  const pathToDoc = `/projects/${projectId}/quotes/${quoteId}`;
  const pathToDocPage = `${pathToDoc}/edit`;
  const match = useMatch(`${pathToDocPage}/items/:itemId`);
  const selectedItemId = match?.params?.itemId ?? null;
  const navigate = useNavigate();
  const location = useLocation();
  const latestLocation = useLatest(location);

  const [additionalItemInput, setAdditionalItemInput] =
    React.useState<AdditionalItemInput | null>(null);
  const additionalItemInputRef = useLatest(additionalItemInput);
  // set when an item is created from a positioned input — the clearing effect
  // re-anchors a new positioned input below it instead of clearing
  const pendingReanchorItemIdRef = React.useRef<string | null>(null);

  const onAdditionalItemInputChange = React.useCallback(
    (input: AdditionalItemInput) => {
      setAdditionalItemInput(input);
      setTimeout(() => {
        const inputRank =
          input.itemRank + (input.position === "below" ? 0.5 : -0.5);
        if (!isInTestMode())
          createRecursiveTryFocusElementFunction(
            `input#msys-tree-input-${input.itemParentType}-${input.itemParentId}[data-node-rank="${inputRank}"]:enabled`
          )();
      });
    },
    []
  );

  const viewer = useUserData().currentUser!;
  const { t } = useTranslate([
    "Global",
    "Quote",
    "ProjectOverview",
    "ItemTree",
  ]);

  const isClientVisibilityEnabled = useFeature("ClientVisibility");
  const viewerPermissionDetailsEnabled = useFeature("ViewerPermissionDetails");

  const { isMinOneColumnWithPreview, isMinTwoColumnsWithPreview } =
    usePageWidth();
  const { isMinTablet } = useScreenWidth();

  const [treeContainer, setTreeContainer] =
    React.useState<HTMLDivElement | null>(null);
  const [previewContainer, setPreviewContainer] =
    React.useState<HTMLDivElement | null>(null);

  const [moveTreeItem, moveTreeItemLoading] = useMoveTreeItem({
    projectId,
    docId: quoteId,
  });

  const expandedStore = useExpandedStoreWithLocalStorage(
    "quote",
    quoteId,
    selectedItemId
  );
  const { areEliminatedItemsShown, setEliminatedItemsShown } =
    useEliminatedItemsStoreWithLocalStorage("quote", quoteId);

  const client = useApolloClient();
  const query = useProjectQuoteEditQuery({
    client,
    variables: {
      projectId,
      quoteId,
      expandedItemIds: expandedStore.expandedItemIds,
    },
  });
  const project = getDataOrNull(query.data?.project)?.project;
  const doc = getDataOrNull(query.data?.quote)?.quote;
  const docTitle = doc?.title ?? "";

  const quoteProductsStatusQuery = useProjectQuoteEditQuoteProductsStatusQuery({
    client,
    variables: {
      projectId,
      quoteId,
    },
  });

  const quoteProductsStatusItems = (
    getDataOrNull(quoteProductsStatusQuery.data?.quoteProductsStatus)?.items ??
    []
  ).filter(e => e.productStatus && e.productStatus.value !== "available");

  const hasSapS4HanaIntegration = useHasSapS4HanaIntegration();

  const isAgreed =
    (doc?.isPublished && doc?.isBinding && doc?.agreement === "YES") ?? false;
  const isViewersQuote =
    doc?.owningSystemOrganisationId === viewer.organisation.id;

  React.useEffect(() => {
    if (!query.loading && !isViewersQuote) {
      navigate(pathToDoc);
    }
  }, [isViewersQuote, navigate, pathToDoc, query.loading]);

  const enableDragging = !isAgreed;

  const {
    filterItemSearch,
    filters,
    setFilters,
    resetFilters,
    search,
    setSearch,
    searchRef,
    toggleSearchVisible,
    searchVisible,
    isSearchOrFiltersEnabled,
    parentItemIds,
    isItemMuted,
  } = useItemsSearch({
    projectId,
    docId: quoteId,
    // when search is getting applied - we need to open tree to all found items
    onSearchApplied: ({ parentItemIds }) => {
      if (
        difference(parentItemIds, expandedStore.expandedItemIds ?? []).length >
        0
      ) {
        expandedStore.setItemsExpanded(parentItemIds, true);
      }
    },
  });

  const showExpandButton = React.useCallback(
    (item: ProjectQuoteEdit_ItemFragment) => {
      if (!allowHaveChildren(item)) return false;
      if (!isSearchOrFiltersEnabled) return item.hasChildren;
      return item.hasChildren && parentItemIds.includes(item.id);
    },
    [isSearchOrFiltersEnabled, parentItemIds]
  );

  const [filteredTreeItems, rootItem, allDocItems] = React.useMemo(
    function getFilteredItems() {
      if (!doc) return [[], undefined, []];
      const allDocItems = doc.items;
      const rootItem = allDocItems?.find(item => item.isRootItem);
      const treeItems = allDocItems
        .filter(item => item.authorOrganisationId === viewer.organisation.id)
        .filter(
          getEliminatedItemsFilter(
            areEliminatedItemsShown,
            selectedItemId,
            allDocItems
          )
        );
      const filteredItems = treeItems.filter(filterItemSearch);
      return [filteredItems, rootItem, allDocItems];
    },
    [
      doc,
      filterItemSearch,
      viewer.organisation.id,
      areEliminatedItemsShown,
      selectedItemId,
    ]
  );

  useUpdateEffect(() => {
    const reanchorItemId = pendingReanchorItemIdRef.current;
    if (reanchorItemId) {
      const newItem = allDocItems?.find(i => i.id === reanchorItemId);
      const newItemParent =
        newItem?.parentId != null
          ? allDocItems?.find(i => i.id === newItem.parentId)
          : null;
      if (newItem && newItem.parentId && newItemParent) {
        // re-open a positioned input below the just-created item to keep adding
        pendingReanchorItemIdRef.current = null;
        onAdditionalItemInputChange({
          itemId: newItem.id,
          itemParentId: newItem.parentId,
          itemParentType: newItemParent.type,
          itemRank: newItem.rank,
          position: "below",
        });
      }
      // keep the pending id until the re-anchor succeeds so a later
      // allDocItems update (once the new item lands) can retry
      return;
    }
    setAdditionalItemInput(null);
  }, [selectedItemId, allDocItems, onAdditionalItemInputChange]);

  useEnrichExpandedStoreWithParentPathIds(
    expandedStore,
    allDocItems,
    selectedItemId,
    !query.loading && !query.error
  );

  const isRootSelected = selectedItemId === rootItem?.id;

  const navigateToItem = React.useCallback(
    (id: string) =>
      navigate(
        {
          pathname: `${pathToDocPage}/items/${id}`,
          search: latestLocation.current.search,
        },
        { replace: true }
      ),
    [latestLocation, navigate, pathToDocPage]
  );
  const navigateToDocPage = React.useCallback(
    () =>
      navigate(
        { pathname: pathToDocPage, search: latestLocation.current.search },
        { replace: true }
      ),
    [latestLocation, navigate, pathToDocPage]
  );

  const quote = getDataOrNull(query.data?.quote)?.quote;

  const viewerPermissions: PermissionName[] = React.useMemo(
    () => quote?.viewerPermissions ?? [],
    [quote?.viewerPermissions]
  );
  const agreement: Agreement | undefined = doc?.agreement;

  const viewerDecisionRole = getViewerDecisionRole(doc?.actors ?? []);

  const confirmProcessRef = React.useRef<ConfirmProcessRef>(null);
  const startConfirmProcess = React.useCallback((props: ConfirmModalProps) => {
    return confirmProcessRef.current!.startConfirmProcess(props);
  }, []);

  const decisionsInBulkProcessRef =
    React.useRef<DecisionsInBulkProcessRef>(null);
  const startDecisionsInBulkProcess = React.useCallback(() => {
    decisionsInBulkProcessRef.current?.startDecisionsInBulkProcess();
  }, []);

  const { selectWizardContextButton, refetchWizardQuestions } =
    useProjectQuoteContext();

  const onItemCreated = React.useCallback(
    async (newItem?: { id: string }) => {
      // if the item was created from a positioned input, remember it so the
      // clearing effect can re-open a positioned input below it
      if (additionalItemInputRef.current && newItem) {
        pendingReanchorItemIdRef.current = newItem.id;
      }
      await refetchWizardQuestions();
    },
    [additionalItemInputRef, refetchWizardQuestions]
  );

  const {
    createItem,
    createItemByType,
    createItemFromTemplate,
    createItemsFromTemplates,
    createItemsWithProducts,
    pasteItem,
    isAllowedToPasteItem,
    fetchItemRecommendations,
    createItemFromRecommendation,
  } = useCreateQuoteItems({
    projectId,
    quoteId,
    expandedItemIds: expandedStore.expandedItemIds,
    onItemCreated,
    source: "edit",
  });

  const TreeItem = React.useMemo(() => {
    if (!quote?.id || agreement === undefined) return nullFn;

    return createTreeItem({
      pathToDocPage,
      projectId,
      doc: {
        id: quote.id,
        rootItemId: quote.rootItem.id,
        viewerPermissions,
        owningSystemOrganisationId: quote.owningSystemOrganisationId,
      },
      docAgreement: agreement,
      startConfirmProcess,
      expandedItemIds: expandedStore.expandedItemIds,
      setItemExpanded: expandedStore.setItemExpanded,
      pasteItem,
      isAllowedToPasteItem,
      onAdditionalItemInputChange,
      getTo: itemProps => ({
        pathname: `${pathToDocPage}/items/${itemProps.item.id}`,
        search: latestLocation.current.search,
      }),
      isItemMuted: itemProps => isItemMuted(itemProps.item),
    });
  }, [
    quote?.id,
    quote?.rootItem.id,
    quote?.owningSystemOrganisationId,
    agreement,
    pathToDocPage,
    projectId,
    viewerPermissions,
    startConfirmProcess,
    expandedStore.expandedItemIds,
    expandedStore.setItemExpanded,
    pasteItem,
    isAllowedToPasteItem,
    onAdditionalItemInputChange,
    latestLocation,
    isItemMuted,
  ]);

  const TreeItemInput = React.useMemo(
    () =>
      createTreeItemInput({
        viewMode: TreeViewMode.Item,
        quoteId,
        createItem,
        createItemByType,
        createItemFromTemplate,
        createItemsFromTemplates,
        createItemsWithProducts,
        pasteItem,
        isAllowedToPasteItem,
        fetchItemRecommendations,
        createItemFromRecommendation,
      }),
    [
      createItem,
      createItemByType,
      createItemFromTemplate,
      createItemsFromTemplates,
      createItemsWithProducts,
      isAllowedToPasteItem,
      pasteItem,
      quoteId,
      fetchItemRecommendations,
      createItemFromRecommendation,
    ]
  );

  // Only the card item tree annotates the empty-placeholder slot; the
  // Subelements card receives the same TreeItemInput via outlet context and is
  // deliberately left unannotated.
  const getRowAnnotation = React.useMemo(
    () =>
      getEmptyPlaceholderRowAnnotation(
        t("Empty — no content chosen yet.", { ns: "ItemTree" })
      ),
    [t]
  );

  // redirect to edit route when selected item is filtered out
  React.useEffect(() => {
    if (
      !query.loading &&
      selectedItemId &&
      !filteredTreeItems.some(item => item.id === selectedItemId) &&
      selectedItemId !== rootItem?.id
    ) {
      navigateToDocPage();
    }
  }, [
    selectedItemId,
    filteredTreeItems,
    rootItem,
    query.loading,
    navigateToDocPage,
  ]);

  const filterItem = React.useCallback(
    (i: ProjectQuoteEdit_ItemFragment) =>
      isTreePreviewItemVisible(true, i, allDocItems, doc?.isBinding ?? true),
    [allDocItems, doc?.isBinding]
  );

  const docSharingEnabled = useFeature("DocSharing");

  return (
    <Page
      subtitle={project?.title}
      title={docTitle}
      submenuItems={submenuItems}
      breadcrumbs={prefixBreadcrumbs}
      header={
        <PageHeader
          breadcrumbs={prefixBreadcrumbs}
          submenuItems={submenuItems}
          activeSubmenuItem={activeSubmenuItem}
        />
      }
      subHeader={
        project && doc ? (
          <QuoteSubHeader
            project={project}
            quote={doc}
            activeView="tree"
            canEdit={isViewersQuote}
            isReadOnly={isAgreed}
            pathToDocList={pathToDocList}
            pathToDoc={pathToDoc}
            hasSapS4HanaIntegration={hasSapS4HanaIntegration}
            isHeaderVisible={undefined as never}
            setHeaderVisible={undefined as never}
            expandedItemIds={expandedStore.expandedItemIds}
            onUpdateDataRefetchQueries={REFETCH_QUERIES}
          />
        ) : undefined
      }
      action={
        project &&
        doc && (
          <QuoteAction
            doc={doc}
            project={project}
            onDiscardPendingChangesRefetchQueries={REFETCH_QUERIES}
            onDeclineChangesRefetchQueries={REFETCH_QUERIES}
            startDecisionsInBulkProcess={startDecisionsInBulkProcess}
            expandedItemIds={expandedStore.expandedItemIds}
            viewerDecisionRole={viewerDecisionRole}
          />
        )
      }
    >
      <PageColumn
        $hasRightBorder
        $hasBackground
        $display="block"
        ref={isMinTablet ? setTreeContainer : undefined}
      >
        {doc && project && rootItem && (
          <Stack direction={"column"} spacing={2}>
            <QuoteAnnotationsBanner />
            {doc.decliningReason && (
              <Box>
                <QuoteDeclinedAlert reason={doc.decliningReason} />
              </Box>
            )}

            {doc.changeDecliningReason && (
              <Box>
                <QuoteChangeDeclinedAlert
                  projectId={projectId}
                  quoteId={quoteId}
                  itemId={rootItem!.id}
                  reason={doc.changeDecliningReason}
                  refetchQueries={REFETCH_QUERIES}
                />
              </Box>
            )}

            {quoteProductsStatusItems.length > 0 && (
              <QuoteProductsAvailabilityAlert
                items={quoteProductsStatusItems}
                navigateToItem={navigateToItem}
              />
            )}

            {doc.incomingRequest &&
              doc.incomingRequest.status !== "PUBLISHED" &&
              doc.incomingRequest.owningSystemOrganisationId !==
                viewer.organisation.id &&
              !doc.isPublished && (
                <Box>
                  <Alert variant="outlined" severity="warning">
                    {t("This opportunity is no longer accepting new quotes", {
                      ns: "ProjectOverview",
                    })}
                  </Alert>
                </Box>
              )}

            <Stack
              direction="row"
              spacing={1}
              sx={{
                width: "100%",
                alignItems: "center",
              }}
            >
              <TreeToggleAllExpandedButton
                areAllItemsExpanded={expandedStore.areAllItemsExpanded}
                setAllItemsExpanded={expandedStore.setAllItemsExpanded}
              />
              <TreeToggleEliminatedItemsButton
                areEliminatedItemsShown={areEliminatedItemsShown}
                setEliminatedItemsShown={setEliminatedItemsShown}
              />
              <ButtonItemTypeSelectMultiple
                value={filters.type ?? []}
                onChange={value => {
                  setFilters(f => ({
                    ...f,
                    type: value.length > 0 ? value : undefined,
                  }));
                }}
              />
              <ButtonItemBindingSelectMultiple
                value={[
                  ...(filters.binding ?? []),
                  ...(filters.needsAgreement ? ["change" as ItemChange] : []),
                ]}
                onChange={value => {
                  const bindings = value.filter(v => v !== "change");
                  const needsAgreement = value.some(v => v === "change");
                  setFilters(f => ({
                    ...f,
                    binding: bindings.length > 0 ? bindings : undefined,
                    needsAgreement: needsAgreement || undefined,
                  }));
                }}
                hasAgreement={doc?.agreement === "YES"}
              />
              {selectWizardContextButton}
              {!searchVisible && (
                <ListHeaderSearchButton onClick={toggleSearchVisible} />
              )}
            </Stack>

            <ItemSearchChips
              filters={filters}
              setFilters={setFilters}
              onReset={resetFilters}
            />

            {searchVisible && (
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  width: "100%",
                  alignItems: "center",
                }}
              >
                <DebouncedSearchInput
                  inputRef={searchRef}
                  placeholder={t("Search", { ns: "Global" })}
                  defaultValue={search}
                  onChangeSearchTerm={setSearch}
                />
                <ListHeaderCloseButton onClick={toggleSearchVisible} />
              </Stack>
            )}

            <Box>
              <VirtualBareTreeStandaloneItem
                item={rootItem}
                items={filteredTreeItems}
                allItems={allDocItems}
                depth={0}
                itemComponent={TreeItem}
                selected={isRootSelected}
                sticky={isRootSelected}
                top={0}
                clickable
                onClick={() => navigateToItem(rootItem.id)}
                isMuted={isItemMuted(rootItem)}
              />

              <VirtualItemTree<ProjectQuoteEdit_ItemFragment>
                moveTreeItem={enableDragging ? moveTreeItem : undefined}
                moveTreeItemLoading={moveTreeItemLoading}
                items={filteredTreeItems}
                allItems={allDocItems}
                filterItem={
                  isClientVisibilityEnabled || !isViewersQuote
                    ? filterItem
                    : undefined
                }
                selectedItemId={selectedItemId}
                enableCreating={true}
                documentItemTypes={QUOTE_ITEM_TYPES}
                container={treeContainer}
                itemComponent={TreeItem}
                inputComponent={isViewersQuote ? TreeItemInput : nullFn}
                getRowAnnotation={getRowAnnotation}
                allowHaveChildren={isViewersQuote ? allowHaveChildren : falseFn}
                shouldRenderCreateInput={
                  isViewersQuote ? shouldRenderCreateInput : falseFn
                }
                expandedStore={expandedStore}
                additionalItemInput={additionalItemInput}
                showExpandButton={showExpandButton}
              />
            </Box>

            <QuoteDecisionsInBulkProcess
              ref={decisionsInBulkProcessRef}
              quoteId={doc.id}
              projectId={projectId}
              canFinalize={true}
              externalExpandedItemIds={expandedStore.expandedItemIds}
              shouldRenderItem={item =>
                (isViewersQuote && !isClientVisibilityEnabled) || !item.isHidden
              }
            />

            {doc.proposedCalculation && (
              <QuoteTimeSummaryBox
                key={`quote_time_summary-${isAgreed}`}
                calculated={doc.proposedCalculation}
              />
            )}

            {doc.proposedCalculation && (
              <QuotePriceSummaryBox
                key={`quote_price_summary-${isAgreed}`}
                isManualSave={isAgreed}
                quoteId={quoteId}
                quote={doc}
                project={project}
                discountPercentage={doc.discountPercentage}
                calculated={doc.proposedCalculation}
              />
            )}

            {doc.proposedCalculation && (
              <ProfitabilityBox calculated={doc.proposedCalculation} />
            )}

            <VanMarckeProfitability projectId={projectId} docId={quoteId} />
          </Stack>
        )}
      </PageColumn>
      <ConfirmProcess ref={confirmProcessRef} />
      <Outlet
        context={{
          expandedItemIds: expandedStore.expandedItemIds,
          CreateChildComponent: TreeItemInput,
          pasteItem,
          isAllowedToPasteItem,
          viewerDecisionRole,
          // Only needed by the detail panel's "Fill placeholder" CTA
          // (TemplatePlaceholderEmptyStateBox) — the tree row's icon does
          // not use the new picker.
          createItemsWithProducts,
          createItemsFromTemplates,
        }}
      />
      {project && doc && isMinOneColumnWithPreview && (
        <PageContainer
          $fullHeight
          onClick={() => {
            if (!isMinTwoColumnsWithPreview) navigateToDocPage();
          }}
          ref={setPreviewContainer}
        >
          <Container maxWidth="md">
            {viewerPermissionDetailsEnabled ? (
              <div>
                <h2>viewer permission details</h2>
                <hr />
                <textarea
                  style={{ width: "100%", height: 50 }}
                  readOnly
                  defaultValue={JSON.stringify(doc.viewerPermissions, null, 2)}
                ></textarea>
              </div>
            ) : null}
            {docSharingEnabled ? (
              <DocShareBox
                docType="QUOTE"
                docId={doc.id}
                projectId={project.id}
              />
            ) : null}

            <QuotePreview
              projectId={projectId}
              navigateToItem={({ id }) => navigateToItem(id)}
              selectedItemId={selectedItemId}
              quote={doc}
              container={previewContainer}
              expandedStore={expandedStore}
              expandedItemIds={expandedStore.expandedItemIds}
              hasSapS4HanaIntegration={hasSapS4HanaIntegration}
              refetchAfterApplyItemAction={refetchWizardQuestions}
              canEdit={isViewersQuote && !isAgreed}
            />
          </Container>
        </PageContainer>
      )}
    </Page>
  );
};
